/* prettier-ignore-file */
import React, { useState, useEffect, useMemo } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  ScrollView,
  ActivityIndicator,
  Platform,
  Alert,
  Modal,
} from "react-native";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, setDoc, deleteDoc } from "firebase/firestore";
import { Timestamp } from "firebase/firestore";
import { db } from "../firebase.config";
import { getCollectionPath } from "../utils/dbPaths";
import { updateBookingFieldsDirect } from "../db_calls";
import {
  parseFirestoreDate,
  cleanPhoneNumber,
  formatPhoneNumberProgressiveDashes,
} from "../utils/formatting";
import AuthHeader from "../components/AuthHeader";
import { useAuthStore, useBookings, useProducts, useSettings } from "../stores";

/** Format date as YYYY-MM-DD in Eastern (UTC-4) to match how booking dates are stored (e.g. "June 15 at 8:00 PM UTC-4"). */
const toDateInputValue = (date) => {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-CA", { timeZone: "America/New_York" });
};

const QTY_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

/** Dropdown quantity selector 0-10 (clamps value to 0-10). */
const QtySelect = ({ value, onChange, style, changed }) => {
  const clamped = Math.max(0, Math.min(10, value));
  if (Platform.OS === "web") {
    return (
      <select
        value={clamped}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        style={{
          minWidth: 56,
          padding: "8px 12px",
          fontSize: 16,
          border: `1px solid ${changed ? "#0E7C6B" : "#DDE3E2"}`,
          borderRadius: 5,
          backgroundColor: changed ? "#E3F2EF" : "#FFFFFF",
          cursor: "pointer",
        }}
      >
        {QTY_OPTIONS.map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
    );
  }
  return (
    <TextInput
      style={[styles.qtyInput, changed && styles.qtyInputChanged, style]}
      value={String(clamped)}
      onChangeText={(text) => {
        const v = Math.max(0, Math.min(10, parseInt(text, 10) || 0));
        onChange(v);
      }}
      keyboardType="number-pad"
      placeholder="0"
    />
  );
};

/** Date field with original vs changed display. hasError: red when start > end. */
const DateField = ({ label, value, onChange, initialValue, style, hasError }) => {
  const changed = value !== initialValue && (value || initialValue);
  const showError = !!hasError;
  const inputStyle = {
    display: "block",
    width: "100%",
    boxSizing: "border-box",
    backgroundColor: showError ? "#FBEAE9" : changed ? "#E3F2EF" : "#FFFFFF",
    border: `1px solid ${showError ? "#C4453D" : changed ? "#0E7C6B" : "#DDE3E2"}`,
    borderRadius: 8,
    padding: "12px 14px",
    minHeight: 44,
    fontSize: 16,
    color: "#0F1A19",
    cursor: "pointer",
  };
  if (Platform.OS === "web") {
    return (
      <View style={style}>
        <Text style={styles.label}>{label}</Text>
        {changed && !showError && (
          <Text style={styles.dateChangeHint}>
            was {initialValue || "—"} → now {value || "—"}
          </Text>
        )}
        <input
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        />
      </View>
    );
  }
  return (
    <View style={style}>
      <Text style={styles.label}>{label}</Text>
      {changed && !showError && (
        <Text style={styles.dateChangeHint}>
          was {initialValue || "—"} → now {value || "—"}
        </Text>
      )}
      <TextInput
        style={[
          styles.input,
          { minHeight: 44 },
          changed && !showError && styles.dateInputChanged,
          showError && styles.dateInputError,
        ]}
        value={value}
        onChangeText={onChange}
        placeholder="YYYY-MM-DD"
        placeholderTextColor="#8A9794"
      />
    </View>
  );
};

const DirectEditBooking = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const authChecked = useAuthStore((s) => s.authChecked);

  const [booking, setBooking] = useState(null);
  const [isFromCompletedBookings, setIsFromCompletedBookings] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [initialStartDate, setInitialStartDate] = useState("");
  const [initialEndDate, setInitialEndDate] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [addressNotes, setAddressNotes] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [gatedCommunity, setGatedCommunity] = useState(false);
  const [delivery, setDelivery] = useState(false);
  const [homePickup, setHomePickup] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [product1Quantities, setProduct1Quantities] = useState({});
  const [product2Quantities, setProduct2Quantities] = useState({});
  const [initialProduct1Quantities, setInitialProduct1Quantities] = useState({});
  const [initialProduct2Quantities, setInitialProduct2Quantities] = useState({});
  const [isDesktop, setIsDesktop] = useState(typeof window !== "undefined" && window.innerWidth >= 768);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const bookingsFromStore = useBookings((s) => s.bookings);
  const inventory = useProducts((s) => s.products1) || [];
  const addOns = useProducts((s) => s.products2) || [];
  const settings = useSettings((s) => s.settings);
  const product1Title = settings?.product1Title?.title ?? "Bicycles";
  const product2Title = settings?.product2Title?.title ?? "Add-Ons";

  // Availability: same logic as New Booking (overlapping bookings, exclude current, include current's edited qty)
  const { availableBikes, availableAddOns } = useMemo(() => {
    if (!startDate || !endDate || !inventory.length) {
      return { availableBikes: [], availableAddOns: addOns };
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (start > end) return { availableBikes: [], availableAddOns: addOns };

    const parseBookingDate = (d) => {
      if (!d) return null;
      if (d.toDate && typeof d.toDate === "function") return d.toDate();
      if (d.seconds) return new Date(d.seconds * 1000);
      return new Date(d);
    };

    const overlappingBookings = bookingsFromStore.filter((b) => {
      const bId = b.bookingId || b.id;
      if (bId === bookingId) return false;
      const bStart = parseBookingDate(b.startDate);
      const bEnd = parseBookingDate(b.endDate);
      if (!bStart || !bEnd) return false;
      bStart.setHours(0, 0, 0, 0);
      bEnd.setHours(0, 0, 0, 0);
      const s = new Date(start);
      const e = new Date(end);
      s.setHours(0, 0, 0, 0);
      e.setHours(0, 0, 0, 0);
      return bStart <= e && bEnd >= s;
    });

    const bookedQuantities = {};
    inventory.forEach((bike) => { bookedQuantities[bike.name] = 0; });
    const bookedAddOnQuantities = {};
    addOns.forEach((addOn) => {
      if (addOn.inventory != null && addOn.inventory !== undefined) {
        bookedAddOnQuantities[addOn.id] = 0;
      }
    });

    overlappingBookings.forEach((b) => {
      const product1Items = b.product1;
      if (product1Items && Array.isArray(product1Items)) {
        product1Items.forEach((bike) => {
          if (Object.prototype.hasOwnProperty.call(bookedQuantities, bike.name)) {
            const qty = bike.quantity > 0 ? bike.quantity : 1;
            bookedQuantities[bike.name] += qty;
          }
        });
      }
      const product2Items = b.product2;
      if (product2Items) {
        if (Array.isArray(product2Items)) {
          product2Items.forEach((item) => {
            const addOnId = item.id;
            if (Object.prototype.hasOwnProperty.call(bookedAddOnQuantities, addOnId)) {
              const addOn = addOns.find((a) => a.id === addOnId);
              if (addOn && addOn.option === "user-selection") {
                bookedAddOnQuantities[addOnId] += item.quantity || 0;
              }
            }
          });
        } else {
          Object.entries(product2Items).forEach(([addOnId, value]) => {
            if (Object.prototype.hasOwnProperty.call(bookedAddOnQuantities, addOnId)) {
              const addOn = addOns.find((a) => a.id === addOnId);
              if (addOn && addOn.option === "user-selection" && value?.quantity) {
                bookedAddOnQuantities[addOnId] += value.quantity || 0;
              }
            }
          });
        }
      }
    });

    // Add current booking's (edited) quantities to booked count
    Object.entries(product1Quantities).forEach(([name, qty]) => {
      if (qty > 0 && Object.prototype.hasOwnProperty.call(bookedQuantities, name)) {
        bookedQuantities[name] += qty;
      }
    });
    Object.entries(product2Quantities).forEach(([addOnId, qty]) => {
      if (qty > 0 && Object.prototype.hasOwnProperty.call(bookedAddOnQuantities, addOnId)) {
        bookedAddOnQuantities[addOnId] += qty;
      }
    });

    const availability = inventory.map((bike) => {
      const booked = bookedQuantities[bike.name] || 0;
      const available = Math.max(0, bike.quantity - booked);
      return { ...bike, booked, available };
    });
    const updatedAddOns = addOns.map((addOn) => {
      if (addOn.inventory != null && addOn.inventory !== undefined) {
        const booked = bookedAddOnQuantities[addOn.id] || 0;
        const available = Math.max(0, addOn.inventory - booked);
        return { ...addOn, booked, available, originalInventory: addOn.inventory };
      }
      return addOn;
    });

    return { availableBikes: availability, availableAddOns: updatedAddOns };
  }, [startDate, endDate, inventory, addOns, bookingsFromStore, bookingId, product1Quantities, product2Quantities]);

  // Auth: only authorized users can use this page
  useEffect(() => {
    if (authChecked && !isAuthenticated && bookingId) {
      navigate(`/view-booking/${bookingId}`, { replace: true });
    }
  }, [authChecked, isAuthenticated, bookingId, navigate]);

  // Load booking from Firestore (bookings first, then completedBookings)
  useEffect(() => {
    if (!bookingId || !isAuthenticated) {
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const bookingsPath = getCollectionPath("bookings");
        let snap = await getDoc(doc(db, bookingsPath, bookingId));
        if (snap.exists()) {
          setBooking({ id: snap.id, ...snap.data() });
          setIsFromCompletedBookings(false);
        } else {
          const completedPath = getCollectionPath("completedBookings");
          snap = await getDoc(doc(db, completedPath, bookingId));
          if (snap.exists()) {
            setBooking({ id: snap.id, ...snap.data() });
            setIsFromCompletedBookings(true);
          } else {
            setError("Booking not found");
            setBooking(null);
          }
        }
      } catch (err) {
        console.error("DirectEditBooking load error:", err);
        setError("Error loading booking");
        setBooking(null);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [bookingId, isAuthenticated]);

  // Populate form when booking is loaded
  useEffect(() => {
    if (!booking) return;
    const start = parseFirestoreDate(booking.startDate);
    const end = parseFirestoreDate(booking.endDate);
    const startStr = toDateInputValue(start);
    const endStr = toDateInputValue(end);
    setStartDate(startStr);
    setEndDate(endStr);
    setInitialStartDate(startStr);
    setInitialEndDate(endStr);
    setCustomerPhone(cleanPhoneNumber(booking.customerPhone ?? ""));
    setDeliveryAddress(typeof booking.deliveryAddress === "string" ? booking.deliveryAddress : (booking.deliveryAddress ? JSON.stringify(booking.deliveryAddress) : ""));
    setAddressNotes(booking.addressNotes ?? "");
    setCustomerEmail(booking.customerEmail ?? "");
    setGatedCommunity(Boolean(booking.gatedCommunity));
    setDelivery(Boolean(booking.delivery));
    setHomePickup(Boolean(booking.homePickup));
    setAdminNotes(booking.adminNotes ?? "");

    const p1Map = {};
    const product1Items = booking.product1;
    if (product1Items && Array.isArray(product1Items)) {
      product1Items.forEach((item) => {
        const bike = inventory.find((b) => b.id === item.id || b.name === item.name);
        const key = bike ? bike.name : (item.name ?? item.id);
        const qty = item.quantity > 0 ? item.quantity : 1;
        if (key) p1Map[key] = Math.min(10, (p1Map[key] || 0) + qty);
      });
    }
    setProduct1Quantities(p1Map);
    setInitialProduct1Quantities(p1Map);

    const p2Map = {};
    const product2Items = booking.product2;
    if (product2Items) {
      if (Array.isArray(product2Items)) {
        product2Items.forEach((item) => {
          const id = item.id;
          const qty = item.quantity || 0;
          if (id && qty > 0) p2Map[id] = Math.min(10, (p2Map[id] || 0) + qty);
        });
      } else {
        Object.entries(product2Items).forEach(([addOnId, value]) => {
          const qty = value?.quantity ?? (value === true ? 1 : 0);
          if (qty > 0) p2Map[addOnId] = Math.min(10, (p2Map[addOnId] || 0) + qty);
        });
      }
    }
    setProduct2Quantities(p2Map);
    setInitialProduct2Quantities(p2Map);
  }, [booking, inventory]);

  /** Build a Date for the given YYYY-MM-DD at 8:00 PM Eastern to match stored booking dates. */
  const dateStringToTimestamp = (dateStr) => {
    if (!dateStr || dateStr.length < 10) return null;
    const [y, m, d] = dateStr.slice(0, 10).split("-").map(Number);
    if (!y || !m || !d) return null;
    const nextDayUtcMidnight = new Date(Date.UTC(y, m - 1, d + 1, 0, 0, 0));
    if (isNaN(nextDayUtcMidnight.getTime())) return null;
    return nextDayUtcMidnight;
  };

  const handleSave = async () => {
    if (!bookingId || !booking) return;

    if (startDate && endDate && endDate < startDate) {
      const msg = "The end date must be the same day or later than the start date.";
      if (Platform.OS === "web") {
        window.alert("Invalid dates\n\n" + msg);
      } else {
        Alert.alert("Invalid dates", msg, [{ text: "OK" }]);
      }
      return;
    }

    setSaving(true);
    setSaveMessage(null);
    setError(null);
    try {
      const collectionPath = getCollectionPath(isFromCompletedBookings ? "completedBookings" : "bookings");
      const startDateObj = dateStringToTimestamp(startDate);
      const endDateObj = dateStringToTimestamp(endDate);

      const product1Array = [];
      Object.entries(product1Quantities).forEach(([bikeName, quantity]) => {
        if (quantity > 0) {
          const bike = inventory.find((b) => b.name === bikeName);
          if (bike) {
            product1Array.push({ id: bike.id, name: bike.name, quantity });
          }
        }
      });

      const product2Array = [];
      Object.entries(product2Quantities).forEach(([addOnId, quantity]) => {
        if (quantity > 0) {
          const addOn = addOns.find((a) => a.id === addOnId);
          if (addOn) {
            product2Array.push({
              id: addOnId,
              name: addOn.name,
              quantity,
              option: addOn.option,
              pricePaid: addOn.price || 0,
            });
          }
        }
      });

      await updateBookingFieldsDirect(collectionPath, bookingId, {
        ...(startDateObj && !isNaN(startDateObj.getTime()) && { startDate: Timestamp.fromDate(startDateObj) }),
        ...(endDateObj && !isNaN(endDateObj.getTime()) && { endDate: Timestamp.fromDate(endDateObj) }),
        customerPhone: customerPhone.trim() || null,
        deliveryAddress: deliveryAddress.trim() || null,
        addressNotes: addressNotes.trim() || null,
        customerEmail: customerEmail.trim() || null,
        gatedCommunity,
        delivery,
        homePickup,
        product1: product1Array,
        product2: product2Array,
        adminNotes: adminNotes.trim() || null,
      });
      setSaveMessage("Saved.");
      setInitialStartDate(startDate);
      setInitialEndDate(endDate);
      setInitialProduct1Quantities({ ...product1Quantities });
      setInitialProduct2Quantities({ ...product2Quantities });
    } catch (err) {
      console.error("DirectEditBooking save error:", err);
      setError(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDeleteBooking = async () => {
    if (!bookingId || isFromCompletedBookings) return;
    setDeleteModalVisible(false);
    setDeleting(true);
    try {
      const bookingsPath = getCollectionPath("bookings");
      const completedBookingsPath = getCollectionPath("completedBookings");
      const bookingRef = doc(db, bookingsPath, bookingId);
      const bookingDoc = await getDoc(bookingRef);
      if (!bookingDoc.exists()) {
        if (Platform.OS === "web") {
          window.alert("Booking not found in active bookings.");
        } else {
          Alert.alert("Error", "Booking not found in active bookings.");
        }
        return;
      }
      const bookingData = bookingDoc.data();
      const deletedBookingData = {
        ...bookingData,
        cancelled: true,
        cancelledAt: Timestamp.now(),
        deleted: true,
        deletedAt: Timestamp.now(),
        deletedViaDirectEdit: true,
      };
      const completedBookingRef = doc(db, completedBookingsPath, bookingId);
      await setDoc(completedBookingRef, deletedBookingData);
      await deleteDoc(bookingRef);
      if (Platform.OS === "web") {
        window.alert("Booking removed. It has been moved to completed bookings and marked as deleted (no email or SMS sent). You can restore it later from completed bookings.");
      } else {
        Alert.alert(
          "Booking removed",
          "It has been moved to completed bookings and marked as deleted (no email or SMS sent). You can restore it later from completed bookings.",
          [{ text: "OK" }]
        );
      }
      navigate("/admin");
    } catch (err) {
      console.error("DirectEditBooking delete error:", err);
      if (Platform.OS === "web") {
        window.alert("Failed to delete booking: " + (err.message || err));
      } else {
        Alert.alert("Error", "Failed to delete booking: " + (err.message || err));
      }
    } finally {
      setDeleting(false);
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <View style={styles.container}>
        <AuthHeader title="Direct Edit Booking" isDesktop={isDesktop} isAuthenticated={isAuthenticated} />
        <View style={[styles.centered, isDesktop && styles.desktopWrapper]}>
          <ActivityIndicator size="large" color="#0E7C6B" />
          <Text style={styles.loadingText}>Loading booking…</Text>
        </View>
      </View>
    );
  }

  if (error && !booking) {
    return (
      <View style={styles.container}>
        <AuthHeader title="Direct Edit Booking" isDesktop={isDesktop} isAuthenticated={isAuthenticated} />
        <View style={[styles.centered, isDesktop && styles.desktopWrapper]}>
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity dataSet={{ hover: "lift" }} style={styles.button} onPress={() => navigate(`/view-booking/${bookingId}`)}>
            <Text style={styles.buttonText}>Back to Booking</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <AuthHeader title="Direct Edit Booking" isDesktop={isDesktop} isAuthenticated={isAuthenticated} />
      <View style={isDesktop ? styles.desktopWrapper : null}>
      <ScrollView style={styles.scroll} contentContainerStyle={styles.content}>
        {bookingId ? (
          <Text style={styles.bookingIdText}>Booking ID: {bookingId}</Text>
        ) : null}
        <Text style={styles.hint}>Edit fields below and tap Save Edits. No payment or booking flow is used.</Text>

        <DateField
          label="Start date"
          value={startDate}
          onChange={setStartDate}
          initialValue={initialStartDate}
          style={styles.field}
          hasError={!!(startDate && endDate && startDate > endDate)}
        />
        <DateField
          label="End date"
          value={endDate}
          onChange={setEndDate}
          initialValue={initialEndDate}
          style={styles.field}
          hasError={!!(startDate && endDate && startDate > endDate)}
        />
        <View style={styles.field}>
          <Text style={styles.label}>Customer phone</Text>
          <TextInput
            style={styles.input}
            value={formatPhoneNumberProgressiveDashes(customerPhone)}
            onChangeText={(text) => setCustomerPhone(cleanPhoneNumber(text).slice(0, 10))}
            placeholder="xxx-xxx-xxxx"
            placeholderTextColor="#8A9794"
            keyboardType="phone-pad"
          />
        </View>

        <View style={styles.checkboxRow}>
          <TouchableOpacity
            style={[styles.gatedCheckbox, delivery && styles.gatedCheckboxChecked]}
            onPress={() => setDelivery((prev) => !prev)}
            activeOpacity={0.7}
          >
            {delivery && <Text style={styles.gatedCheckmark}>✓</Text>}
          </TouchableOpacity>
          <Text style={styles.gatedLabel}>Delivery</Text>
        </View>
        <View style={styles.checkboxRow}>
          <TouchableOpacity
            style={[styles.gatedCheckbox, homePickup && styles.gatedCheckboxChecked]}
            onPress={() => setHomePickup((prev) => !prev)}
            activeOpacity={0.7}
          >
            {homePickup && <Text style={styles.gatedCheckmark}>✓</Text>}
          </TouchableOpacity>
          <Text style={styles.gatedLabel}>Home pickup</Text>
        </View>

        <View style={styles.field}>
          <Text style={styles.label}>Customer address</Text>
          <TextInput
            style={[styles.input, styles.inputMultiline]}
            value={deliveryAddress}
            onChangeText={setDeliveryAddress}
            placeholder="Delivery address"
            placeholderTextColor="#8A9794"
            multiline
          />
        </View>
        <View style={styles.field}>
          <Text style={styles.label}>Address notes</Text>
          <TextInput
            style={[styles.input, styles.inputMultiline]}
            value={addressNotes}
            onChangeText={setAddressNotes}
            placeholder="Address notes"
            placeholderTextColor="#8A9794"
            multiline
          />
        </View>

        <View style={styles.gatedCommunityRow}>
          <TouchableOpacity
            style={[styles.gatedCheckbox, gatedCommunity && styles.gatedCheckboxChecked]}
            onPress={() => setGatedCommunity((prev) => !prev)}
            activeOpacity={0.7}
          >
            {gatedCommunity && <Text style={styles.gatedCheckmark}>✓</Text>}
          </TouchableOpacity>
          <Text style={styles.gatedLabel}>Gated Community</Text>
        </View>

        <View style={styles.field}>
          <Text style={styles.label}>Customer email</Text>
          <TextInput
            style={styles.input}
            value={customerEmail}
            onChangeText={setCustomerEmail}
            placeholder="Email"
            placeholderTextColor="#8A9794"
            keyboardType="email-address"
            autoCapitalize="none"
          />
        </View>

        {startDate && endDate && availableBikes.length > 0 && (
          <View style={styles.productSection}>
            <Text style={styles.productSectionTitle}>{product1Title}</Text>
            {availableBikes.map((bike) => {
              const current = product1Quantities[bike.name] ?? 0;
              const initial = initialProduct1Quantities[bike.name] ?? 0;
              const changed = current !== initial;
              return (
                <View key={bike.name} style={styles.productRow}>
                  <View style={styles.productRowLeft}>
                    <Text style={styles.productName}>{bike.name}</Text>
                    <Text style={styles.productMeta}>
                      {bike.available} available
                      {changed ? (
                        <Text style={styles.productChange}>
                          {" "}· was {initial} → now {current}
                        </Text>
                      ) : (
                        <Text style={styles.productMeta}> · booking: {current}</Text>
                      )}
                    </Text>
                  </View>
                  <QtySelect
                    value={current}
                    onChange={(v) => setProduct1Quantities((prev) => ({ ...prev, [bike.name]: v }))}
                    changed={changed}
                  />
                </View>
              );
            })}
          </View>
        )}

        {startDate && endDate && availableAddOns.length > 0 && (
          <View style={styles.productSection}>
            <Text style={styles.productSectionTitle}>{product2Title}</Text>
            {availableAddOns.map((addOn) => {
              const hasInventory = addOn.inventory != null && addOn.inventory !== undefined;
              const current = product2Quantities[addOn.id] ?? 0;
              const initial = initialProduct2Quantities[addOn.id] ?? 0;
              const changed = current !== initial;
              return (
                <View key={addOn.id} style={styles.productRow}>
                  <View style={styles.productRowLeft}>
                    <Text style={styles.productName}>{addOn.name}</Text>
                    <Text style={styles.productMeta}>
                      {hasInventory ? `${addOn.available ?? addOn.inventory} available` : "—"}
                      {changed ? (
                        <Text style={styles.productChange}>
                          {" "}· was {initial} → now {current}
                        </Text>
                      ) : (
                        <Text style={styles.productMeta}> · booking: {current}</Text>
                      )}
                    </Text>
                  </View>
                  <QtySelect
                    value={current}
                    onChange={(v) => setProduct2Quantities((prev) => ({ ...prev, [addOn.id]: v }))}
                    changed={changed}
                  />
                </View>
              );
            })}
          </View>
        )}

        <View style={styles.field}>
          <Text style={styles.label}>Admin notes</Text>
          <TextInput
            style={[styles.input, styles.inputMultiline]}
            value={adminNotes}
            onChangeText={setAdminNotes}
            placeholder="Admin notes (staff only)"
            placeholderTextColor="#8A9794"
            multiline
          />
        </View>

        {error ? <Text style={styles.errorText}>{error}</Text> : null}
        {saveMessage ? <Text style={styles.successText}>{saveMessage}</Text> : null}

        <TouchableOpacity dataSet={{ hover: "lift" }}
          style={[styles.button, styles.primaryButton]}
          onPress={handleSave}
          disabled={saving}
        >
          {saving ? (
            <ActivityIndicator size="small" color="#FFFFFF" />
          ) : (
            <Text style={styles.primaryButtonText}>Save Edits</Text>
          )}
        </TouchableOpacity>

        <TouchableOpacity dataSet={{ hover: "lift" }} style={[styles.button, styles.backButtonOrange]} onPress={() => navigate(`/view-booking/${bookingId}`)}>
          <Text style={styles.backButtonOrangeText}>Back to View Booking</Text>
        </TouchableOpacity>

        {!isFromCompletedBookings && (
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[styles.button, styles.deleteButton]}
            onPress={() => setDeleteModalVisible(true)}
            disabled={deleting}
          >
            {deleting ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <Text style={styles.deleteButtonText}>Delete Booking</Text>
            )}
          </TouchableOpacity>
        )}

        <Modal
          visible={deleteModalVisible}
          transparent
          animationType="fade"
          onRequestClose={() => setDeleteModalVisible(false)}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.modalBox}>
              <Text style={styles.modalTitle}>Delete Booking</Text>
              <Text style={styles.modalMessage}>
                Move this booking to completed and mark it as deleted? No email or SMS will be sent. You can restore it later from completed bookings.
              </Text>
              <View style={styles.modalButtons}>
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.modalButton, styles.modalButtonCancel]}
                  onPress={() => setDeleteModalVisible(false)}
                >
                  <Text style={styles.modalButtonCancelText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.modalButton, styles.modalButtonConfirm]}
                  onPress={handleConfirmDeleteBooking}
                >
                  <Text style={styles.modalButtonConfirmText}>Delete Booking</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>
      </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: "#F6F8F7" },
  desktopWrapper: {
    width: "100%",
    maxWidth: 800,
    flex: 1,
    alignSelf: "center",
  },
  scroll: { flex: 1 },
  content: { padding: 16, paddingBottom: 32 },
  bookingIdText: {
    fontSize: 12,
    color: "#5C6B68",
    marginBottom: 12,
    fontFamily: Platform.OS === "web" ? "monospace" : undefined,
  },
  centered: { flex: 1, justifyContent: "center", alignItems: "center", padding: 20 },
  loadingText: { marginTop: 12, fontSize: 16, color: "#5C6B68" },
  hint: { fontSize: 14, color: "#5C6B68", marginBottom: 16 },
  field: { marginBottom: 16 },
  label: { fontSize: 14, fontWeight: "600", color: "#2E3A38", marginBottom: 6 },
  dateChangeHint: {
    fontSize: 13,
    color: "#0E7C6B",
    fontWeight: "500",
    marginBottom: 6,
  },
  dateInputChanged: { borderColor: "#0E7C6B", backgroundColor: "#E3F2EF" },
  dateInputError: { borderColor: "#C4453D", backgroundColor: "#FBEAE9" },
  input: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    padding: 12,
    fontSize: 16,
  },
  inputMultiline: { minHeight: 60, textAlignVertical: "top" },
  gatedCommunityRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
  },
  checkboxRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
  },
  gatedCheckbox: {
    width: 24,
    height: 24,
    borderWidth: 2,
    borderColor: "#DDE3E2",
    borderRadius: 4,
    marginRight: 10,
    justifyContent: "center",
    alignItems: "center",
  },
  gatedCheckboxChecked: {
    backgroundColor: "#0E7C6B",
    borderColor: "#0E7C6B",
  },
  gatedCheckmark: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "bold",
  },
  gatedLabel: { fontSize: 16, color: "#2E3A38" },
  productSection: { marginBottom: 20 },
  productSectionTitle: { fontSize: 18, fontWeight: "600", color: "#2E3A38", marginBottom: 10 },
  productRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    padding: 12,
    marginBottom: 8,
  },
  productRowLeft: { flex: 1, marginRight: 12 },
  productName: { fontSize: 16, fontWeight: "500", color: "#0F1A19" },
  productMeta: { fontSize: 13, color: "#5C6B68", marginTop: 2 },
  productChange: { color: "#0E7C6B", fontWeight: "500" },
  qtyInput: {
    width: 56,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 5,
    padding: 8,
    fontSize: 16,
    textAlign: "center",
  },
  qtyInputChanged: { borderColor: "#0E7C6B", backgroundColor: "#E3F2EF" },
  errorText: { color: "#C4453D", marginBottom: 12, fontSize: 14 },
  successText: { color: "#1F8A5B", marginBottom: 12, fontSize: 14 },
  button: {
    backgroundColor: "#DDE3E2",
    padding: 14,
    borderRadius: 6,
    alignItems: "center",
    marginTop: 8,
  },
  buttonText: { fontSize: 16, color: "#2E3A38" },
  primaryButton: { backgroundColor: "#0E7C6B", marginTop: 16 },
  primaryButtonText: { fontSize: 16, color: "#FFFFFF", fontWeight: "600" },
  backButtonOrange: { backgroundColor: "#B4761B" },
  backButtonOrangeText: { fontSize: 16, color: "#FFFFFF", fontWeight: "500" },
  deleteButton: { backgroundColor: "#C4453D", marginTop: 16 },
  deleteButtonText: { fontSize: 16, color: "#FFFFFF", fontWeight: "600" },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.5)",
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  modalBox: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 24,
    maxWidth: 400,
    width: "100%",
  },
  modalTitle: { fontSize: 18, fontWeight: "700", color: "#2E3A38", marginBottom: 12 },
  modalMessage: { fontSize: 15, color: "#5C6B68", marginBottom: 20, lineHeight: 22 },
  modalButtons: { flexDirection: "row", justifyContent: "flex-end" },
  modalButton: { paddingVertical: 12, paddingHorizontal: 20, borderRadius: 6 },
  modalButtonCancel: { backgroundColor: "#DDE3E2" },
  modalButtonCancelText: { fontSize: 16, color: "#2E3A38", fontWeight: "500" },
  modalButtonConfirm: { backgroundColor: "#C4453D", marginLeft: 12 },
  modalButtonConfirmText: { fontSize: 16, color: "#FFFFFF", fontWeight: "600" },
});

export default DirectEditBooking;
